import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useWorkspaceSession } from '@/hooks/useWorkspaceSession'
import { useAuth } from '@/store/auth.store'
import { useOrganizationStore } from '@/store/organization.store'
import { useQueryClient } from '@tanstack/react-query'

import { SocketEvent, Task } from '@scrambl/shared'

import { useRealtime } from '../core/useRealtime'
import { getTaskEditingState } from '../task-editing-state'
import {
  isTaskCollectionNotice,
  subscribeTaskCollectionInvalidation,
} from '../task-query-invalidation'
import { taskReaderProjection } from '../task-reader-projection'

// Add task events to broadcast types
export enum TaskBroadcastEventType {
  TASK_CREATED = 'task:created',
  TASK_UPDATED = 'task:updated',
  TASK_DELETED = 'task:deleted',
  TASK_STATUS_UPDATED = 'task:status_updated',
  TASK_ASSIGNED = 'task:assigned',
  TASK_UNASSIGNED = 'task:unassigned',
  RECURRING_TASK_CREATED = 'task:recurring_created',
}

export interface TaskRealtimeOptions {
  onTaskCreated?: (task: Task) => void
  onTaskUpdated?: (task: Task) => void
  onTaskDeleted?: (data: { id: string; organizationId: string }) => void
  onTaskStatusChanged?: (data: {
    taskId: string
    oldStatus: string
    newStatus: string
    task: Task
  }) => void
  onTaskAssigned?: (data: {
    taskId: string
    userId: string
    task: Task
  }) => void
  onTaskUnassigned?: (data: {
    taskId: string
    userId: string
    task: Task
  }) => void
  onRecurringTaskCreated?: (data: {
    parentTaskId: string
    newTask: Task
    instanceNumber: number
  }) => void
  enableBroadcastChannel?: boolean
  enabled?: boolean
}

/**
 * Hook for real-time task synchronization
 * Listens to task events for the current organization and user
 */
export function useTaskRealtime(options: TaskRealtimeOptions = {}) {
  const {
    onTaskCreated,
    onTaskUpdated,
    onTaskDeleted,
    onTaskStatusChanged,
    onTaskAssigned,
    onTaskUnassigned,
    onRecurringTaskCreated,
    enableBroadcastChannel = true,
    enabled = true,
  } = options

  const queryClient = useQueryClient()
  const scope = useWorkspaceSession()
  const editing = useMemo(
    () => getTaskEditingState(queryClient, scope),
    [queryClient, scope],
  )
  const recoveryScope = useMemo(
    () => ({
      ...scope,
      isEditing: () => editing.hasAnyEditing(),
      subscribeEditing: (listener: () => void) =>
        editing.subscribeEditing(listener),
    }),
    [scope, editing],
  )
  const organizationId = useOrganizationStore().currentOrganization?.id
  const notices = useRef<ReturnType<
    typeof subscribeTaskCollectionInvalidation
  > | null>(null)
  useEffect(() => {
    if (!enabled || !scope.enabled || !organizationId) return
    const subscription = subscribeTaskCollectionInvalidation(
      queryClient,
      organizationId,
      recoveryScope,
    )
    notices.current = subscription
    return () => {
      notices.current = null
      subscription.dispose()
    }
  }, [enabled, queryClient, organizationId, scope, recoveryScope])
  const acceptsEvent = useCallback(
    (data: unknown) => {
      if (
        !enabled ||
        !scope.enabled ||
        scope.signal.aborted ||
        !organizationId ||
        !data ||
        typeof data !== 'object'
      )
        return false
      try {
        scope.assertUnchanged()
      } catch {
        return false
      }
      const event = data as {
        organizationId?: unknown
        task?: Task
        newTask?: Task
      }
      const organizations = [
        event.organizationId,
        event.task?.organizationId,
        event.newTask?.organizationId,
      ].filter(id => id !== undefined)
      // An envelope cannot contradict its nested task or borrow the selected
      // workspace when all source identity is missing.
      return (
        organizations.length > 0 &&
        organizations.every(id => id === organizationId)
      )
    },
    [enabled, scope, organizationId],
  )
  const requestTaskRefresh = useCallback(
    (eventId?: string, readerDenied = false) => {
      if (!enabled || !scope.enabled || scope.signal.aborted) return
      try {
        scope.assertUnchanged()
      } catch {
        return
      }
      notices.current?.notify(eventId, readerDenied)
    },
    [enabled, scope],
  )
  const consumeNotice = useCallback(
    (data: unknown) => {
      if (!isTaskCollectionNotice(data)) return false
      if (acceptsEvent(data))
        requestTaskRefresh(data._eventId, data._readerDenied === true)
      return true
    },
    [acceptsEvent, requestTaskRefresh],
  )

  const { user } = useAuth.getState()
  const userId = user?.id

  const {
    socket,
    isReady,
    joinRoom,
    leaveRoom,
    addEventListener,
    broadcastReaderInvalidation,
  } = useRealtime({
    enableBroadcastChannel,
    suppressLogging: false, // Always log task events as they're critical for debugging
  })

  const publishNotice = useCallback(
    (event: string, data: unknown) => {
      if (!acceptsEvent(data) || !organizationId) return
      // Preserve identity only on an already identifier-free notice. A
      // legacy row payload's event identity could be its record ID.
      const id = isTaskCollectionNotice(data) ? data._eventId : undefined
      broadcastReaderInvalidation(
        event,
        'task',
        organizationId,
        typeof id === 'string' ? id : undefined,
        isTaskCollectionNotice(data) && data._readerDenied === true,
      )
    },
    [acceptsEvent, organizationId, broadcastReaderInvalidation],
  )

  // Keep refs for callbacks to avoid re-subscribing
  const callbackRefs = useRef({
    onTaskCreated,
    onTaskUpdated,
    onTaskDeleted,
    onTaskStatusChanged,
    onTaskAssigned,
    onTaskUnassigned,
    onRecurringTaskCreated,
  })

  // Update refs when callbacks change
  useEffect(() => {
    callbackRefs.current = {
      onTaskCreated,
      onTaskUpdated,
      onTaskDeleted,
      onTaskStatusChanged,
      onTaskAssigned,
      onTaskUnassigned,
      onRecurringTaskCreated,
    }
  }, [
    onTaskCreated,
    onTaskUpdated,
    onTaskDeleted,
    onTaskStatusChanged,
    onTaskAssigned,
    onTaskUnassigned,
    onRecurringTaskCreated,
  ])

  // Join the user's room when ready
  useEffect(() => {
    if (!enabled || !isReady || !userId) {
      return
    }

    // Join user room to receive task events
    // Note: User rooms are automatically joined by backend, so joinRoom may return false
    const joined = joinRoom('user', userId)
    if (!joined) {
      // This is expected behavior - user rooms are auto-joined by backend
      // Debug only
    } else {
      // Debug only
    }

    return () => {
      leaveRoom('user', userId)
    }
  }, [enabled, isReady, userId, joinRoom, leaveRoom])

  const reconcileReaderProjection = useCallback(
    (data: Task) => {
      if (!Object.hasOwn(data, '_readerProjection')) return true // Legacy sparse patch.
      const projection = taskReaderProjection(data)
      if (!projection || !data.id) {
        requestTaskRefresh(undefined, true)
        return false
      }
      editing.reconcileReaderProjection(data.id, projection)
      return true
    },
    [editing, requestTaskRefresh],
  )

  // Socket payloads are already recipient views. Cross-tab messages carry
  // only reader recovery hints, since the other tab may have another actor.
  const handleTaskCreated = useCallback(
    (data: Task) => {
      if (!acceptsEvent(data)) return
      publishNotice(SocketEvent.TASK_CREATED, data)
      if (!consumeNotice(data) && reconcileReaderProjection(data))
        callbackRefs.current.onTaskCreated?.(data)
    },
    [acceptsEvent, publishNotice, consumeNotice, reconcileReaderProjection],
  )
  const handleTaskUpdated = useCallback(
    (data: Task) => {
      if (!acceptsEvent(data)) return
      publishNotice(SocketEvent.TASK_UPDATED, data)
      if (!consumeNotice(data) && reconcileReaderProjection(data))
        callbackRefs.current.onTaskUpdated?.(data)
    },
    [acceptsEvent, publishNotice, consumeNotice, reconcileReaderProjection],
  )
  const handleTaskDeleted = useCallback(
    (data: { id: string; organizationId: string }) => {
      if (!acceptsEvent(data)) return
      publishNotice(SocketEvent.TASK_DELETED, data)
      if (!consumeNotice(data)) callbackRefs.current.onTaskDeleted?.(data)
    },
    [acceptsEvent, publishNotice, consumeNotice],
  )
  const handleTaskStatusChanged = useCallback(
    (
      data: Parameters<
        NonNullable<TaskRealtimeOptions['onTaskStatusChanged']>
      >[0],
    ) => {
      if (!acceptsEvent(data)) return
      publishNotice(SocketEvent.TASK_STATUS_UPDATED, data)
      if (!data.task) consumeNotice({ ...data, _bulk: true })
      else callbackRefs.current.onTaskStatusChanged?.(data)
    },
    [acceptsEvent, publishNotice, consumeNotice],
  )
  const handleTaskAssigned = useCallback(
    (
      data: Parameters<NonNullable<TaskRealtimeOptions['onTaskAssigned']>>[0],
    ) => {
      if (!acceptsEvent(data)) return
      publishNotice(SocketEvent.TASK_ASSIGNED, data)
      if (!data.task) consumeNotice({ ...data, _bulk: true })
      else callbackRefs.current.onTaskAssigned?.(data)
    },
    [acceptsEvent, publishNotice, consumeNotice],
  )
  const handleTaskUnassigned = useCallback(
    (
      data: Parameters<NonNullable<TaskRealtimeOptions['onTaskUnassigned']>>[0],
    ) => {
      if (!acceptsEvent(data)) return
      publishNotice(SocketEvent.TASK_UNASSIGNED, data)
      if (!data.task) consumeNotice({ ...data, _bulk: true })
      else callbackRefs.current.onTaskUnassigned?.(data)
    },
    [acceptsEvent, publishNotice, consumeNotice],
  )
  const handleRecurringTaskCreated = useCallback(
    (data: { parentTaskId: string; newTask: Task; instanceNumber: number }) => {
      if (!acceptsEvent(data)) return
      publishNotice(SocketEvent.RECURRING_TASK_CREATED, data)
      callbackRefs.current.onRecurringTaskCreated?.(data)
    },
    [acceptsEvent, publishNotice],
  )

  // Set up socket listeners
  useEffect(() => {
    if (!enabled || !socket || !isReady) {
      return
    }

    // Debug only

    // Add socket event listeners
    socket.on(SocketEvent.TASK_CREATED, handleTaskCreated)
    socket.on(SocketEvent.TASK_UPDATED, handleTaskUpdated)
    socket.on(SocketEvent.TASK_DELETED, handleTaskDeleted)
    socket.on(SocketEvent.TASK_STATUS_UPDATED, handleTaskStatusChanged)
    socket.on(SocketEvent.TASK_ASSIGNED, handleTaskAssigned)
    socket.on(SocketEvent.TASK_ASSIGNEE_ADDED, handleTaskAssigned)
    socket.on(SocketEvent.TASK_ASSIGNEE_REMOVED, handleTaskUnassigned)
    socket.on(SocketEvent.TASK_UNASSIGNED, handleTaskUnassigned)
    socket.on(SocketEvent.RECURRING_TASK_CREATED, handleRecurringTaskCreated)

    return () => {
      socket.off(SocketEvent.TASK_CREATED, handleTaskCreated)
      socket.off(SocketEvent.TASK_UPDATED, handleTaskUpdated)
      socket.off(SocketEvent.TASK_DELETED, handleTaskDeleted)
      socket.off(SocketEvent.TASK_STATUS_UPDATED, handleTaskStatusChanged)
      socket.off(SocketEvent.TASK_ASSIGNED, handleTaskAssigned)
      socket.off(SocketEvent.TASK_ASSIGNEE_ADDED, handleTaskAssigned)
      socket.off(SocketEvent.TASK_ASSIGNEE_REMOVED, handleTaskUnassigned)
      socket.off(SocketEvent.TASK_UNASSIGNED, handleTaskUnassigned)
      socket.off(SocketEvent.RECURRING_TASK_CREATED, handleRecurringTaskCreated)
    }
  }, [
    enabled,
    socket,
    isReady,
    handleTaskCreated,
    handleTaskUpdated,
    handleTaskDeleted,
    handleTaskStatusChanged,
    handleTaskAssigned,
    handleTaskUnassigned,
    handleRecurringTaskCreated,
  ])

  // Set up broadcast channel listeners
  useEffect(() => {
    if (!enabled || !enableBroadcastChannel) {
      return
    }

    // Add broadcast event listeners for cross-tab sync
    const cleanupFns: (() => void)[] = []
    const addScopedEventListener = (
      event: string,
      listener: (data: unknown) => void,
    ) =>
      addEventListener(event, data => {
        if (acceptsEvent(data)) listener(data)
      })

    for (const event of Object.values(TaskBroadcastEventType)) {
      cleanupFns.push(
        addScopedEventListener(event, data => {
          // Legacy tabs may still send raw task snapshots. Treat those solely
          // as hints too: field/record policy is re-evaluated by this tab's API.
          if (!consumeNotice(data)) requestTaskRefresh()
        }),
      )
    }

    return () => {
      cleanupFns.forEach(cleanup => cleanup())
    }
  }, [
    enabled,
    addEventListener,
    enableBroadcastChannel,
    consumeNotice,
    acceptsEvent,
    requestTaskRefresh,
  ])

  return {
    isReady,
    requestTaskRefresh,
  }
}
